import type { CustomerNotificationIndicatorResponse } from '@kizen/api/customer-notifications';
import type { NavIconProps } from '__components/Kizen/Icon';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { memo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { NavIcon } from '__components/Kizen/Icon';
import { monitoringMessageHelper } from 'instrumentation/helpers';
import { INDICATOR_COLORS } from 'pages/Notifications/colors';
import {
  customerNotificationsIndicatorQuery,
  customerNotificationsInfiniteQuery,
} from 'query/customer-notifications';
import { DEFAULT_PATHS } from 'routes/paths';

type Status = CustomerNotificationIndicatorResponse['status'];

type NotificationIconProps = {
  enabled?: boolean;
};

interface BellNavIconProps extends NavIconProps {
  runShakeAnimation?: boolean;
}

const ShakableBellIcon = styled(NavIcon)<BellNavIconProps>`
  ${({ runShakeAnimation }) => {
    if (runShakeAnimation) {
      return css`
        animation: shake ease-in-out 2s;
        @keyframes shake {
          0% {
            transform: rotate(0deg);
          }
          10% {
            transform: rotate(8deg);
          }
          20% {
            transform: rotate(-8deg);
          }
          30% {
            transform: rotate(6deg);
          }
          40% {
            transform: rotate(-6deg);
          }
          50% {
            transform: rotate(4deg);
          }
          60% {
            transform: rotate(-4deg);
          }
          70% {
            transform: rotate(2deg);
          }
          80% {
            transform: rotate(-2deg);
          }
          90% {
            transform: rotate(1deg);
          }
          100% {
            transform: rotate(0deg);
          }
        }
      `;
    }
  }}
`;

export const NotificationIcon = memo((props: NotificationIconProps) => {
  const { enabled = true } = props;

  const qc = useQueryClient();
  const { pathname } = useLocation();
  const prevStatus = useRef<Status>();

  const query = useQuery(
    customerNotificationsIndicatorQuery({
      enabled,
      refetchInterval: 30_000,
      retry: false,
      throwOnError: false,
    })
  );

  const onNotificationsPage = pathname.startsWith(DEFAULT_PATHS.NOTIFICATIONS);
  const status = query.data?.status ?? 'none';
  const showBadge = status !== 'none';
  const badgeColor = showBadge ? INDICATOR_COLORS[status] : undefined;
  const shake =
    !onNotificationsPage &&
    enabled &&
    showBadge &&
    prevStatus.current !== status;
  prevStatus.current = status;

  if (shake) {
    qc.removeQueries({
      queryKey: customerNotificationsInfiniteQuery().queryKey,
    });
  }

  if (enabled && query.isError) {
    monitoringMessageHelper('Notification indicator request failed');
  }

  return (
    <ShakableBellIcon
      icon="bell"
      size="1x"
      className="fa-w-16"
      key={status}
      runShakeAnimation={shake}
      withBadge={showBadge}
      badgeColor={badgeColor}
      badgeHeight={14}
    />
  );
});
